import {useCallback, useEffect, useState} from "react";
import {useOutletContext} from "react-router-dom";
import {APIv1} from "../../api";
import {Table} from "antd";
import {ConvertLogsPaymentProvider} from "../../utils/logsUtils";
import {useTranslation} from "react-i18next";
import {extractDateBySecond} from "../../utils";
import LogsColumns from "./logs.constants";
import useUrlTableState from "../../hooks/useUrlTableState";
import ListStateIndicator from "../../components/ListStateIndicator/ListStateIndicator";
import LogsExpandedRow from "../../components/PaymentLogs/LogsExpandedRow";

const LOGS_FILTER_PARAM_MAP = {
    status: 'status',
    logType: 'provider',
};

const LOGS_FILTER_LABELS = {
    status: 'Status',
    provider: 'Provider',
};

const Logs = (props) => {
    let defaultPaginationSize = props.defaultPaginationSize !== undefined ? props.defaultPaginationSize : 20;
    let companyInn = props.companyInn;

    const {t} = useTranslation();
    const [userData, setUserData] = useState({});
    const [logsData, setLogsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startPeriod, setStartPeriod] = useState(0);
    const [endPeriod, setEndPeriod] = useState(0);
    const [totalLogs, setTotalLogs] = useState(0);
    const {searchText} = useOutletContext();
    const tableState = useUrlTableState({
        defaultPageSize: defaultPaginationSize,
        filterParamMap: LOGS_FILTER_PARAM_MAP,
        filterLabels: LOGS_FILTER_LABELS,
        searchText,
    });

    const handleChangePeriod = (value) => {
        if (value === null || value === 0) {
            setStartPeriod(0);
            setEndPeriod(0);
        } else {
            let startPeriod = value[0].format('YYYY-MM-DD');
            let endPeriod = value[1].add(1, 'days').format('YYYY-MM-DD');
            setStartPeriod(startPeriod);
            setEndPeriod(endPeriod);
        }
    }

    const columns = LogsColumns(t, handleChangePeriod);
    const controlledColumns = tableState.controlledColumns(columns);

    const fetchLogsData = useCallback(async (page, size, search = '', ordering = '', filters = {}) => {
        setLoading(true);
        try {
            let pre_url = companyInn !== undefined ? `payment/logs/list/get_related_logs/?company_inn=${companyInn}` : 'payment/logs/list/';
            let url = (startPeriod !== 0 && endPeriod !== 0)
                ? `${pre_url}?start_period=${startPeriod}&end_period=${endPeriod}`
                : pre_url;

            const queryParams = {
                page,
                page_size: size,
                search,
                ordering,
            };

            if (filters.status) {
                queryParams.status = Array.isArray(filters.status) ? filters.status[0] : filters.status;
            }

            if (filters.period) {
                queryParams.period = Array.isArray(filters.period) ? filters.period[0] : filters.period;
            }

            if (filters.provider || filters.logType) {
                const providerVal = filters.provider || filters.logType;
                queryParams.provider = Array.isArray(providerVal) ? providerVal[0] : providerVal;
            }

            const response = await APIv1.get(url, {
                params: queryParams,
                headers: {
                    Authorization: `Token ${userData.token}`,
                }
            });
            const data = response.data.results.map((log) => ({
                ...log,
                key: log.id,
                deviceSerial: log.device_serial === 'None' ? 'Unknown' : log.device_serial,
                companyName: log.company_name,
                companyPublicName: log.company_public_name,
                paymentId: log.payment_id === null ? 'Unknown' : log.payment_id,
                amount: log.amount,
                isSuccess: log.is_success,
                status: log.status,
                logType: ConvertLogsPaymentProvider(log.log_type),
                paymentResponse: log.failure_payment_response,
                confirmResponse: log.failure_confirm_response,
                createdDate: extractDateBySecond(log.created_date),
            }));

            setLogsData(data);
            setTotalLogs(response.data.count);
        } catch (err) {
            console.error('Something went wrong:', err);
        } finally {
            setLoading(false);
        }
    }, [companyInn, userData.token, startPeriod, endPeriod]);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items) {
            setUserData(items);
        }
    }, [userData.token]);

    useEffect(() => {
        if (!userData.token) return;

        fetchLogsData(tableState.currentPage, tableState.pageSize, tableState.search, tableState.ordering, tableState.filters);
    }, [tableState.currentPage, tableState.pageSize, tableState.search, tableState.ordering, userData.token, tableState.filters, fetchLogsData, startPeriod, endPeriod]);

    const tableChangeHandler = tableState.handleTableChange(columns);

    return (
        <>
            <div className="content_container">
                <ListStateIndicator items={tableState.stateIndicators} onReset={tableState.resetState} onRemove={tableState.removeState}/>
                <Table
                    expandable={{
                        expandedRowRender: (record) => (
                            <LogsExpandedRow record={record} token={userData.token}/>
                        ),
                        rowExpandable: (record) => record.name !== 'Not Expandable',
                    }}
                    columns={controlledColumns}
                    dataSource={logsData}
                    loading={loading}
                    onChange={(pagination, filters, sorter, extra) => {
                        tableChangeHandler(pagination, filters, sorter, extra);
                    }}
                    pagination={{
                        total: totalLogs,
                        current: tableState.currentPage,
                        pageSize: tableState.pageSize,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]} - ${range[1]} / ${total}`,
                        pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                />
            </div>
        </>
    );
};

export default Logs;
